import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createInitialRoadmapData, validateStep, normalizeSmartGoal } from '../data/models';
import apiService from '../utils/api';

// Module-level variables to handle debouncing and concurrency
let _saveTimeout = null;
let _isSaving = false;

const mergeRoadmapData = (incoming = {}) => {
  const base = createInitialRoadmapData();
  const mergedSteps = Object.keys(base.steps).reduce((acc, key) => {
    acc[key] = {
      ...base.steps[key],
      ...(incoming.steps?.[key] || {})
    };
    return acc;
  }, {});

  return {
    ...base,
    ...incoming,
    steps: mergedSteps,
    smart_goals: (incoming.smart_goals || []).map(normalizeSmartGoal),
    created_date: incoming.created_date || base.created_date,
    last_modified: incoming.last_modified || base.last_modified
  };
};

const useRoadmapStore = create(
  persist(
    (set, get) => ({
      // State
      roadmapData: createInitialRoadmapData(),
      currentStep: 'vision',
      currentRoadmapId: null,
      roadmaps: [],
      isLoading: false,
      error: null,
      userAccessLevel: 'edit', // 'edit' or 'view'

      // Actions
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      setRoadmapData: (data) => set({ roadmapData: data }),
      setCurrentRoadmapId: (id) => set({ currentRoadmapId: id }),
      setRoadmaps: (roadmaps) => set({ roadmaps }),
      setUserAccessLevel: (level) => set({ userAccessLevel: level }),
      
      // Helper to check if user can edit
      canEdit: () => get().userAccessLevel === 'edit',

      // API Actions
      loadRoadmaps: async () => {
        try {
          set({ isLoading: true, error: null });
          const roadmaps = await apiService.getAllRoadmaps();
          set({ roadmaps });
          
          // If user has no current roadmap loaded but has roadmaps, don't auto-load
          // Let them choose or create a new one
          const { currentRoadmapId } = get();
          if (currentRoadmapId && roadmaps.length > 0) {
            // Check if current roadmap still exists
            const exists = roadmaps.find(r => r._id === currentRoadmapId);
            if (exists) {
              await get().loadRoadmap(currentRoadmapId);
            } else {
              // Current roadmap no longer exists, reset
              set({ 
                roadmapData: createInitialRoadmapData(),
                currentRoadmapId: null 
              });
            }
          } else if (!currentRoadmapId) {
            // No roadmap selected, reset to initial state
            set({ roadmapData: createInitialRoadmapData() });
          }
        } catch (error) {
          set({ error: error.message });
          console.error('Failed to load roadmaps:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      loadRoadmap: async (id) => {
        try {
          set({ isLoading: true, error: null });
          const response = await apiService.getRoadmap(id);
          const roadmapData = response.data;
          const userAccessLevel = response.userAccessLevel || 'edit';
          
          set({
            roadmapData,
            currentRoadmapId: id,
            currentStep: 'vision',
            userAccessLevel
          });
          get().updateProgress();
        } catch (error) {
          set({ error: error.message });
          console.error('Failed to load roadmap:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      saveRoadmap: async () => {
        // Prevent concurrent saves
        if (_isSaving) return;

        const { roadmapData, currentRoadmapId } = get();

        // Don't attempt to save until we have a project name
        if (!roadmapData.project_name || !roadmapData.project_name.trim()) return;

        _isSaving = true;
        try {
          set({ isLoading: true, error: null });

          if (currentRoadmapId) {
            // Update existing — do NOT overwrite local state with server response
            await apiService.updateRoadmap(currentRoadmapId, roadmapData);
          } else {
            // Create new
            const savedRoadmap = await apiService.createRoadmap(roadmapData);
            set({ currentRoadmapId: savedRoadmap._id });
          }

          // Refresh sidebar list silently
          try {
            const roadmaps = await apiService.getAllRoadmaps();
            set({ roadmaps });
          } catch (_) { /* ignore list refresh errors */ }
        } catch (error) {
          set({ error: error.message });
          console.error('Failed to save roadmap:', error);
        } finally {
          set({ isLoading: false });
          _isSaving = false;
        }
      },

      // Debounced auto-save — replaces the raw setTimeout calls
      _scheduleSave: () => {
        if (_saveTimeout) clearTimeout(_saveTimeout);
        _saveTimeout = setTimeout(() => {
          _saveTimeout = null;
          get().saveRoadmap();
        }, 1000);
      },

      deleteRoadmap: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await apiService.deleteRoadmap(id);

          // If we deleted the current roadmap, reset to initial state
          if (get().currentRoadmapId === id) {
            set({
              roadmapData: createInitialRoadmapData(),
              currentRoadmapId: null,
              currentStep: 'vision'
            });
          }

          await get().loadRoadmaps(); // Refresh the roadmaps list
        } catch (error) {
          set({ error: error.message });
          console.error('Failed to delete roadmap:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      updateRoadmapData: (updates) => {
        set((state) => ({
          roadmapData: {
            ...state.roadmapData,
            ...updates,
            last_modified: new Date().toISOString().split('T')[0]
          }
        }));
        get().updateProgress();
        get()._scheduleSave();
      },

      updateStepData: (stepName, stepUpdates) => {
        set((state) => ({
          roadmapData: {
            ...state.roadmapData,
            steps: {
              ...state.roadmapData.steps,
              [stepName]: {
                ...state.roadmapData.steps[stepName],
                ...stepUpdates
              }
            },
            last_modified: new Date().toISOString().split('T')[0]
          }
        }));
        get().updateProgress();
        get()._scheduleSave();
      },

      markStepComplete: (stepName, isComplete) => {
        const { roadmapData, currentRoadmapId } = get();
        const stepData = roadmapData.steps[stepName];

        // Auto-validate if marking complete
        const isValid = isComplete ? validateStep(stepName, stepData) : true;

        set((state) => ({
          roadmapData: {
            ...state.roadmapData,
            steps: {
              ...state.roadmapData.steps,
              [stepName]: {
                ...state.roadmapData.steps[stepName],
                completed: isComplete && isValid
              }
            },
            last_modified: new Date().toISOString().split('T')[0]
          }
        }));
        get().updateProgress();

        // Save to backend
        if (currentRoadmapId) {
          apiService.markStepComplete(currentRoadmapId, stepName, isComplete && isValid)
            .catch(error => {
              console.error('Failed to update step completion:', error);
              set({ error: error.message });
            });
        }

        return isComplete && isValid;
      },

      updateProgress: () => {
        const { roadmapData } = get();
        const steps = Object.values(roadmapData.steps);
        const completedSteps = steps.filter(step => step.completed).length;
        const totalSteps = steps.length;
        const progress = Math.round((completedSteps / totalSteps) * 100);

        set((state) => ({
          roadmapData: {
            ...state.roadmapData,
            completion_status: progress
          }
        }));
      },

      setCurrentStep: (stepName) => {
        set({ currentStep: stepName });
      },

      newProject: () => {
        if (_saveTimeout) { clearTimeout(_saveTimeout); _saveTimeout = null; }
        set({
          roadmapData: createInitialRoadmapData(),
          currentRoadmapId: null,
          currentStep: 'vision'
        });
      },

      loadProject: (data) => {
        const merged = mergeRoadmapData(data);
        set({
          roadmapData: merged,
          currentRoadmapId: null,
          currentStep: 'vision'
        });
        get().updateProgress();
      },

      exportProject: () => {
        const { roadmapData } = get();
        const dataStr = JSON.stringify(roadmapData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });

        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${roadmapData.project_name || 'roadmap'}.rmap`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },

      // ── SMART goals ───────────────────────────────────────────────────
      // Stored beside the 10 steps (not inside them) so they never affect
      // step completion or the progress bar.
      getSmartGoals: () => get().roadmapData.smart_goals || [],

      setSmartGoals: (goals) => {
        get().updateRoadmapData({ smart_goals: goals });
      },

      saveSmartGoal: (goal) => {
        const goals = get().roadmapData.smart_goals || [];
        const stamped = { ...goal, last_modified: new Date().toISOString().split('T')[0] };
        const index = goals.findIndex(g => g.id === goal.id);
        const updated = index >= 0
          ? goals.map((g, i) => (i === index ? stamped : g))
          : [...goals, stamped];
        get().updateRoadmapData({ smart_goals: updated });
        return stamped;
      },

      deleteSmartGoal: (goalId) => {
        const goals = get().roadmapData.smart_goals || [];
        get().updateRoadmapData({ smart_goals: goals.filter(g => g.id !== goalId) });
      },

      // Computed values
      getProgress: () => {
        const { roadmapData } = get();
        return roadmapData.completion_status;
      },

      getStepData: (stepName) => {
        const { roadmapData } = get();
        return roadmapData.steps[stepName];
      },

      isStepCompleted: (stepName) => {
        const { roadmapData } = get();
        return roadmapData.steps[stepName]?.completed || false;
      }
    }),
    {
      name: 'roadmap-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentStep: state.currentStep,
        currentRoadmapId: state.currentRoadmapId
      })
    }
  )
);

// Clear persisted roadmap data when user logs out
export const clearRoadmapData = () => {
  const store = useRoadmapStore.getState();
  store.setRoadmapData(createInitialRoadmapData());
  store.setCurrentRoadmapId(null);
  store.setRoadmaps([]);
};

export default useRoadmapStore;
